import {
    useVariable as originalUseVariable,
    useVariableValue as originalUseVariableValue,
    DVCVariable,
    DVCVariableValue,
    DevCycleJSON
} from '@devcycle/react-client-sdk'

declare module '@devcycle/types' {
   interface CustomVariableDefinitions extends DVCVariableTypes {}
}

export type CustomData = {
    'payment_type'?: string
    'use_supplier_name'?: boolean
    'partner_programme'?: string
    'has_buyer_supplier_data'?: boolean
    'has_documents'?: boolean
    'has_contacts'?: boolean
    'has_awards'?: boolean
    'team_name'?: string
    'has_frameworks'?: boolean
    'subscription'?: string
    'team_id'?: string
    'company_name'?: string
    'company_id'?: string
}

export type UseVariableValue = <
    K extends string & keyof DVCVariableTypes
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariableTypes[K]

export const useVariableValue = originalUseVariableValue as UseVariableValue

export type UseVariable = <
    K extends string & keyof DVCVariableTypes,
    T extends DVCVariableValue & DVCVariableTypes[K],
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariable<T>

export const useVariable = originalUseVariable as UseVariable

export type DVCVariableTypes = {
    /**
     * key: tender-documents-semantic-search-version
     * created by: Gabriella Abraham
     * created on: 2025-06-18
    */
    'tender-documents-semantic-search-version': number
    /**
     * key: ai-conversation
     * created by: Fionn McConville
     * created on: 2025-06-18
    */
    'ai-conversation': boolean
    /**
     * key: opportunity-crm
     * created by: Justyna Gredecka
     * created on: 2025-06-12
    */
    'opportunity-crm': boolean
    /**
     * key: buyer-graphql
     * created by: Hasseeb Hussain
     * created on: 2025-06-10
    */
    'buyer-graphql': boolean
    /**
     * key: supplier-graphql
     * created by: Hasseeb Hussain
     * created on: 2025-06-10
    */
    'supplier-graphql': boolean
    /**
     * key: migrate-buyer-supplier-graph-ql
     * created by: Hasseeb Hussain
     * created on: 2025-06-10
    */
    'migrate-buyer-supplier-graph-ql': boolean
    /**
     * key: userflow-top-layer
     * created by: Archie Lamb
     * created on: 2025-06-04
    */
    'userflow-top-layer': boolean
    /**
     * key: opportunity-supplier-relationships
     * created by: Justyna Gredecka
     * created on: 2025-05-16
    */
    'opportunity-supplier-relationships': boolean
    /**
     * key: inline-filters
     * created by: Chris Parsons
     * created on: 2025-05-15
    */
    'inline-filters': boolean
    /**
     * key: buyer-graph-ql
     * created by: Hasseeb Hussain
     * created on: 2025-05-13
    */
    'buyer-graph-ql': boolean
    /**
     * key: max_parallel_document_uploads
     * created by: Fionn McConville
     * created on: 2025-04-25
    */
    'max_parallel_document_uploads': number
    /**
     * key: prompt-key-override
     * created by: Piotr Cwieczkowski
     * created on: 2025-04-24
    */
    'prompt-key-override': string
    /**
     * key: llm-model
     * created by: Piotr Cwieczkowski
     * created on: 2025-04-24
    */
    'llm-model': string
    /**
     * key: dedupe-result-chunks
     * created by: Piotr Cwieczkowski
     * created on: 2025-04-23
    */
    'dedupe-result-chunks': boolean
    /**
     * key: comments-email
     * created by: Swetha Charles
     * created on: 2025-04-09
     * @deprecated This variable is part of complete feature "Opportunity notifications" and should be cleaned up.

    */
    'comments-email': boolean
    /**
     * key: may-4th-activation
     * created by: Fionn McConville
     * created on: 2025-04-07
     * @deprecated This variable is part of complete feature "May 4th Activation" and should be cleaned up.

    */
    'may-4th-activation': boolean
    /**
     * key: market-analysis
     * created by: Archie Lamb
     * created on: 2025-04-02
     * @deprecated This variable is part of complete feature "Market Analysis" and should be cleaned up.

    */
    'market-analysis': boolean
    /**
     * key: keyword-search-function
     * created by: Vishal Bhanderi
     * created on: 2025-04-01
    */
    'keyword-search-function': boolean
    /**
     * key: semantic-search-result-count
     * created by: Vishal Bhanderi
     * created on: 2025-03-31
    */
    'semantic-search-result-count': number
    /**
     * key: opportunity-page-redesign
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2025-03-28
     * @deprecated This variable is part of complete feature "Opportunity magic" and should be cleaned up.

    */
    'opportunity-page-redesign': boolean
    /**
     * key: opportunity-notifications
     * created by: Swetha Charles
     * created on: 2025-03-20
     * @deprecated This variable is part of complete feature "Opportunity notifications" and should be cleaned up.

    */
    'opportunity-notifications': boolean
    /**
     * key: opportunity-buyer-activity
     * created by: Dominic Bruton
     * created on: 2025-03-17
     * @deprecated This variable is part of complete feature "Opportunity magic" and should be cleaned up.

    */
    'opportunity-buyer-activity': boolean
    /**
     * key: onboarding-seo-redirect
     * created by: Chris Parsons
     * created on: 2025-03-17
    */
    'onboarding-seo-redirect': boolean
    /**
     * key: qualify-bid-concurrency
     * description: The number which controls how many qualifyBid questions we run in parallel
     * created by: Fionn McConville
     * created on: 2025-03-14
    */
    'qualify-bid-concurrency': number
    /**
     * key: bid-question-variables
     * description: A list of variables that can be adjusted to improve LLM accuracy
     * created by: Vishal Bhanderi
     * created on: 2025-03-10
    */
    'bid-question-variables': DevCycleJSON
    /**
     * key: procurement-act-lots
     * created by: Archie Lamb
     * created on: 2025-03-07
     * @deprecated This variable is part of complete feature "Procurement Act Lots" and should be cleaned up.

    */
    'procurement-act-lots': boolean
    /**
     * key: context-chunk-size
     * created by: Vishal Bhanderi
     * created on: 2025-02-27
    */
    'context-chunk-size': number
    /**
     * key: procurement-act-timeline
     * created by: Archie Lamb
     * created on: 2025-02-26
     * @deprecated This variable is part of complete feature "Procurement Act Timeline" and should be cleaned up.

    */
    'procurement-act-timeline': boolean
    /**
     * key: hubspot-payment-link
     * created by: Chris Parsons
     * created on: 2025-02-24
    */
    'hubspot-payment-link': boolean
    /**
     * key: procurement-act
     * created by: Archie Lamb
     * created on: 2025-02-14
     * @deprecated This variable is part of complete feature "Procurement Act" and should be cleaned up.

    */
    'procurement-act': boolean
    /**
     * key: notice-details-function
     * created by: Vishal Bhanderi
     * created on: 2025-02-14
    */
    'notice-details-function': boolean
    /**
     * key: organisations-signal-filtering
     * created by: Dominic Bruton
     * created on: 2025-02-13
    */
    'organisations-signal-filtering': boolean
    /**
     * key: bid-document-processor
     * created by: Vishal Bhanderi
     * created on: 2025-02-11
    */
    'bid-document-processor': string
    /**
     * key: load-notice-signals-async
     * created by: Piotr Cwieczkowski
     * created on: 2025-01-22
    */
    'load-notice-signals-async': boolean
    /**
     * key: allow-bid-requalify
     * created by: Fionn McConville
     * created on: 2025-01-13
    */
    'allow-bid-requalify': boolean
    /**
     * key: enable-full-document-search
     * description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
     * created by: Vishal Bhanderi
     * created on: 2025-01-08
    */
    'enable-full-document-search': boolean
    /**
     * key: new-highlight-logic
     * created by: Fionn McConville
     * created on: 2024-10-29
    */
    'new-highlight-logic': boolean
    /**
     * key: process-notifications-job
     * created by: Chris Parsons
     * created on: 2024-10-16
    */
    'process-notifications-job': boolean
    /**
     * key: use-search-engine-for-notifications
     * created by: Gabriella Abraham
     * created on: 2024-09-18
    */
    'use-search-engine-for-notifications': boolean
    /**
     * key: signal-settings-improvements
     * created by: Hasseeb Hussain
     * created on: 2024-09-16
    */
    'signal-settings-improvements': boolean
    /**
     * key: new-supplier-filter
     * created by: Gabriella Abraham
     * created on: 2024-08-16
    */
    'new-supplier-filter': boolean
    /**
     * key: new-notices-search
     * created by: Gabriella Abraham
     * created on: 2024-08-16
    */
    'new-notices-search': boolean
    /**
     * key: cpv-codes-filter
     * created by: Gabriella Abraham
     * created on: 2024-07-22
    */
    'cpv-codes-filter': boolean
    /**
     * key: keywords-on-the-fly
     * created by: Gabriella Abraham
     * created on: 2024-07-22
    */
    'keywords-on-the-fly': boolean
    /**
     * key: layer-cake-filters
     * created by: Gabriella Abraham
     * created on: 2024-07-15
    */
    'layer-cake-filters': boolean
    /**
     * key: documents-signal-score
     * created by: Justyna Gredecka
     * created on: 2024-07-02
    */
    'documents-signal-score': boolean
}

/**
 * key: tender-documents-semantic-search-version
 * created by: Gabriella Abraham
 * created on: 2025-06-18
*/

export const TENDER_DOCUMENTS_SEMANTIC_SEARCH_VERSION = 'tender-documents-semantic-search-version' as const

/**
 * key: ai-conversation
 * created by: Fionn McConville
 * created on: 2025-06-18
*/

export const AI_CONVERSATION = 'ai-conversation' as const

/**
 * key: opportunity-crm
 * created by: Justyna Gredecka
 * created on: 2025-06-12
*/

export const OPPORTUNITY_CRM = 'opportunity-crm' as const

/**
 * key: buyer-graphql
 * created by: Hasseeb Hussain
 * created on: 2025-06-10
*/

export const BUYER_GRAPHQL = 'buyer-graphql' as const

/**
 * key: supplier-graphql
 * created by: Hasseeb Hussain
 * created on: 2025-06-10
*/

export const SUPPLIER_GRAPHQL = 'supplier-graphql' as const

/**
 * key: migrate-buyer-supplier-graph-ql
 * created by: Hasseeb Hussain
 * created on: 2025-06-10
*/

export const MIGRATE_BUYER_SUPPLIER_GRAPH_QL = 'migrate-buyer-supplier-graph-ql' as const

/**
 * key: userflow-top-layer
 * created by: Archie Lamb
 * created on: 2025-06-04
*/

export const USERFLOW_TOP_LAYER = 'userflow-top-layer' as const

/**
 * key: opportunity-supplier-relationships
 * created by: Justyna Gredecka
 * created on: 2025-05-16
*/

export const OPPORTUNITY_SUPPLIER_RELATIONSHIPS = 'opportunity-supplier-relationships' as const

/**
 * key: inline-filters
 * created by: Chris Parsons
 * created on: 2025-05-15
*/

export const INLINE_FILTERS = 'inline-filters' as const

/**
 * key: buyer-graph-ql
 * created by: Hasseeb Hussain
 * created on: 2025-05-13
*/

export const BUYER_GRAPH_QL = 'buyer-graph-ql' as const

/**
 * key: max_parallel_document_uploads
 * created by: Fionn McConville
 * created on: 2025-04-25
*/

export const MAX_PARALLEL_DOCUMENT_UPLOADS = 'max_parallel_document_uploads' as const

/**
 * key: prompt-key-override
 * created by: Piotr Cwieczkowski
 * created on: 2025-04-24
*/

export const PROMPT_KEY_OVERRIDE = 'prompt-key-override' as const

/**
 * key: llm-model
 * created by: Piotr Cwieczkowski
 * created on: 2025-04-24
*/

export const LLM_MODEL = 'llm-model' as const

/**
 * key: dedupe-result-chunks
 * created by: Piotr Cwieczkowski
 * created on: 2025-04-23
*/

export const DEDUPE_RESULT_CHUNKS = 'dedupe-result-chunks' as const

/**
 * key: comments-email
 * created by: Swetha Charles
 * created on: 2025-04-09
 * @deprecated This variable is part of complete feature "Opportunity notifications" and should be cleaned up.

*/

export const COMMENTS_EMAIL = 'comments-email' as const

/**
 * key: may-4th-activation
 * created by: Fionn McConville
 * created on: 2025-04-07
 * @deprecated This variable is part of complete feature "May 4th Activation" and should be cleaned up.

*/

export const MAY_4TH_ACTIVATION = 'may-4th-activation' as const

/**
 * key: market-analysis
 * created by: Archie Lamb
 * created on: 2025-04-02
 * @deprecated This variable is part of complete feature "Market Analysis" and should be cleaned up.

*/

export const MARKET_ANALYSIS = 'market-analysis' as const

/**
 * key: keyword-search-function
 * created by: Vishal Bhanderi
 * created on: 2025-04-01
*/

export const KEYWORD_SEARCH_FUNCTION = 'keyword-search-function' as const

/**
 * key: semantic-search-result-count
 * created by: Vishal Bhanderi
 * created on: 2025-03-31
*/

export const SEMANTIC_SEARCH_RESULT_COUNT = 'semantic-search-result-count' as const

/**
 * key: opportunity-page-redesign
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2025-03-28
 * @deprecated This variable is part of complete feature "Opportunity magic" and should be cleaned up.

*/

export const OPPORTUNITY_PAGE_REDESIGN = 'opportunity-page-redesign' as const

/**
 * key: opportunity-notifications
 * created by: Swetha Charles
 * created on: 2025-03-20
 * @deprecated This variable is part of complete feature "Opportunity notifications" and should be cleaned up.

*/

export const OPPORTUNITY_NOTIFICATIONS = 'opportunity-notifications' as const

/**
 * key: opportunity-buyer-activity
 * created by: Dominic Bruton
 * created on: 2025-03-17
 * @deprecated This variable is part of complete feature "Opportunity magic" and should be cleaned up.

*/

export const OPPORTUNITY_BUYER_ACTIVITY = 'opportunity-buyer-activity' as const

/**
 * key: onboarding-seo-redirect
 * created by: Chris Parsons
 * created on: 2025-03-17
*/

export const ONBOARDING_SEO_REDIRECT = 'onboarding-seo-redirect' as const

/**
 * key: qualify-bid-concurrency
 * description: The number which controls how many qualifyBid questions we run in parallel
 * created by: Fionn McConville
 * created on: 2025-03-14
*/

export const QUALIFY_BID_CONCURRENCY = 'qualify-bid-concurrency' as const

/**
 * key: bid-question-variables
 * description: A list of variables that can be adjusted to improve LLM accuracy
 * created by: Vishal Bhanderi
 * created on: 2025-03-10
*/

export const BID_QUESTION_VARIABLES = 'bid-question-variables' as const

/**
 * key: procurement-act-lots
 * created by: Archie Lamb
 * created on: 2025-03-07
 * @deprecated This variable is part of complete feature "Procurement Act Lots" and should be cleaned up.

*/

export const PROCUREMENT_ACT_LOTS = 'procurement-act-lots' as const

/**
 * key: context-chunk-size
 * created by: Vishal Bhanderi
 * created on: 2025-02-27
*/

export const CONTEXT_CHUNK_SIZE = 'context-chunk-size' as const

/**
 * key: procurement-act-timeline
 * created by: Archie Lamb
 * created on: 2025-02-26
 * @deprecated This variable is part of complete feature "Procurement Act Timeline" and should be cleaned up.

*/

export const PROCUREMENT_ACT_TIMELINE = 'procurement-act-timeline' as const

/**
 * key: hubspot-payment-link
 * created by: Chris Parsons
 * created on: 2025-02-24
*/

export const HUBSPOT_PAYMENT_LINK = 'hubspot-payment-link' as const

/**
 * key: procurement-act
 * created by: Archie Lamb
 * created on: 2025-02-14
 * @deprecated This variable is part of complete feature "Procurement Act" and should be cleaned up.

*/

export const PROCUREMENT_ACT = 'procurement-act' as const

/**
 * key: notice-details-function
 * created by: Vishal Bhanderi
 * created on: 2025-02-14
*/

export const NOTICE_DETAILS_FUNCTION = 'notice-details-function' as const

/**
 * key: organisations-signal-filtering
 * created by: Dominic Bruton
 * created on: 2025-02-13
*/

export const ORGANISATIONS_SIGNAL_FILTERING = 'organisations-signal-filtering' as const

/**
 * key: bid-document-processor
 * created by: Vishal Bhanderi
 * created on: 2025-02-11
*/

export const BID_DOCUMENT_PROCESSOR = 'bid-document-processor' as const

/**
 * key: load-notice-signals-async
 * created by: Piotr Cwieczkowski
 * created on: 2025-01-22
*/

export const LOAD_NOTICE_SIGNALS_ASYNC = 'load-notice-signals-async' as const

/**
 * key: allow-bid-requalify
 * created by: Fionn McConville
 * created on: 2025-01-13
*/

export const ALLOW_BID_REQUALIFY = 'allow-bid-requalify' as const

/**
 * key: enable-full-document-search
 * description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
 * created by: Vishal Bhanderi
 * created on: 2025-01-08
*/

export const ENABLE_FULL_DOCUMENT_SEARCH = 'enable-full-document-search' as const

/**
 * key: new-highlight-logic
 * created by: Fionn McConville
 * created on: 2024-10-29
*/

export const NEW_HIGHLIGHT_LOGIC = 'new-highlight-logic' as const

/**
 * key: process-notifications-job
 * created by: Chris Parsons
 * created on: 2024-10-16
*/

export const PROCESS_NOTIFICATIONS_JOB = 'process-notifications-job' as const

/**
 * key: use-search-engine-for-notifications
 * created by: Gabriella Abraham
 * created on: 2024-09-18
*/

export const USE_SEARCH_ENGINE_FOR_NOTIFICATIONS = 'use-search-engine-for-notifications' as const

/**
 * key: signal-settings-improvements
 * created by: Hasseeb Hussain
 * created on: 2024-09-16
*/

export const SIGNAL_SETTINGS_IMPROVEMENTS = 'signal-settings-improvements' as const

/**
 * key: new-supplier-filter
 * created by: Gabriella Abraham
 * created on: 2024-08-16
*/

export const NEW_SUPPLIER_FILTER = 'new-supplier-filter' as const

/**
 * key: new-notices-search
 * created by: Gabriella Abraham
 * created on: 2024-08-16
*/

export const NEW_NOTICES_SEARCH = 'new-notices-search' as const

/**
 * key: cpv-codes-filter
 * created by: Gabriella Abraham
 * created on: 2024-07-22
*/

export const CPV_CODES_FILTER = 'cpv-codes-filter' as const

/**
 * key: keywords-on-the-fly
 * created by: Gabriella Abraham
 * created on: 2024-07-22
*/

export const KEYWORDS_ON_THE_FLY = 'keywords-on-the-fly' as const

/**
 * key: layer-cake-filters
 * created by: Gabriella Abraham
 * created on: 2024-07-15
*/

export const LAYER_CAKE_FILTERS = 'layer-cake-filters' as const

/**
 * key: documents-signal-score
 * created by: Justyna Gredecka
 * created on: 2024-07-02
*/

export const DOCUMENTS_SIGNAL_SCORE = 'documents-signal-score' as const
